import { DISPLAYABLE_FILE_TYPES, DisplayableFileTypeName } from "../constants/file-types";

type FileIconName = "faFileRegular" | "faFolder" | "faFileZipper" | "faFileWord" | "faFileExcel" | "faFilePdf";

interface FileLike {
	name?: string;
	type?: string;
}

const ZIP_MIME_TYPES: ReadonlySet<string> = new Set([
	"application/zip",
	"application/x-zip-compressed",
	"multipart/x-zip",
	"application/zip-compressed",
]);

const WORD_MIME_TYPES: ReadonlySet<string> = new Set([
	"application/msword", // DOC / DOT (Word 97-2003 Document & Template)
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX (Word Document)
	"application/vnd.openxmlformats-officedocument.wordprocessingml.template", // DOTX (Word Document Template)
	"application/vnd.ms-word.document.macroEnabled.12", // DOCM (Word Macro-Enabled Document)
	"application/vnd.ms-word.template.macroEnabled.12", // DOTM (Word Macro-Enabled Document Template)
	"application/rtf", // RTF (Rich Text Format)
]);

const EXCEL_MIME_TYPES: ReadonlySet<string> = new Set([
	"application/vnd.ms-excel", // XLS / XLT (Excel 97-2003 Workbook & Template)
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX (Excel Workbook)
	"application/vnd.openxmlformats-officedocument.spreadsheetml.template", // XLTX (Excel Template)
	"application/vnd.ms-excel.sheet.macroEnabled.12", // XLSM (Excel Macro-Enabled Workbook)
	"application/vnd.ms-excel.template.macroEnabled.12", // XLTM (Excel Macro-Enabled Template)
	"application/vnd.ms-excel.addin.macroEnabled.12", // XLAM (Excel Add-In)
]);

const PDF_MIME_TYPES: ReadonlySet<string> = new Set([
	"application/pdf", // PDF Document
]);

const ODT_MIME_TYPES: ReadonlySet<string> = new Set([
	"application/vnd.oasis.opendocument.text", // ODT (OpenDocument Text)
]);

const WORD_EXTENSIONS: ReadonlySet<string> = new Set(["doc", "rtf", "docx", "odt"]);
const EXCEL_EXTENSIONS: ReadonlySet<string> = new Set(["xls", "xlsx", "xlsm"]);

const getExtension = (name?: string): string | undefined => name?.split(".").pop()?.toLowerCase();

export const isZipMimeTypes = (type?: string): boolean => !!type && ZIP_MIME_TYPES.has(type);

export const isWordMimeTypes = (type?: string): boolean => !!type && WORD_MIME_TYPES.has(type);

export const isWordExtension = (extension?: string): boolean => !!extension && WORD_EXTENSIONS.has(extension);

export const isWordExtensionByName = (name?: string): boolean => WORD_EXTENSIONS.has(getExtension(name) ?? "");

export const isExcelMimeTypes = (type?: string): boolean => !!type && EXCEL_MIME_TYPES.has(type);

export const isExcelExtension = (extension?: string): boolean => !!extension && EXCEL_EXTENSIONS.has(extension);

export const isExcelExtensionByName = (name?: string): boolean => EXCEL_EXTENSIONS.has(getExtension(name) ?? "");

export const isPdfMimeTypes = (type?: string): boolean => !!type && PDF_MIME_TYPES.has(type);

export const isOdtMimeTypes = (type?: string): boolean => !!type && ODT_MIME_TYPES.has(type);

export const isAllowedFile = (type?: string): boolean =>
	isZipMimeTypes(type) ||
	isWordMimeTypes(type) ||
	isExcelMimeTypes(type) ||
	isPdfMimeTypes(type) ||
	isOdtMimeTypes(type);

export function matchTypeIcon({ name, type }: FileLike): FileIconName {
	if (!type && !name) {
		return "faFileRegular";
	}
	if (type === "folder") {
		return "faFolder";
	}
	if (isZipMimeTypes(type) || type === "zip" || getExtension(name) === "zip") {
		return "faFileZipper";
	}
	if (isWordExtensionByName(name) || isWordMimeTypes(type) || isWordExtension(type)) {
		return "faFileWord";
	}
	if (isExcelExtensionByName(name) || isExcelMimeTypes(type) || isExcelExtension(type)) {
		return "faFileExcel";
	}
	if (isPdfMimeTypes(type) || type === "pdf" || getExtension(name) === "pdf") {
		return "faFilePdf";
	}
	return "faFileRegular";
}

export function isDisplayableFileType(fileType: string): boolean {
	return DISPLAYABLE_FILE_TYPES[fileType as DisplayableFileTypeName]?.displayable ?? false;
}
